import { formatYearsMonthDay } from "@/lib/utility/date-helper";
import {
  MrfiktivPrecedingInvoiceDtoGen,
  MrfiktivPrecedingInvoiceViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";

/**
 * A reference to an invoice this document reacts to (EN 16931 BG-3).
 *
 * A credit note or a correction must name the invoice it credits or corrects,
 * by number (BT-25) and optionally by issue date (BT-26). `invoiceId` links the
 * reference to an invoice in the system; references entered by hand for
 * documents that were never recorded here carry the number only.
 */
export class PrecedingInvoice implements MrfiktivPrecedingInvoiceViewModelGen {
  /** The number of the preceding invoice (BT-25) */
  number: string;

  /** The issue date of the preceding invoice (BT-26) */
  issueDate?: string;

  /** The linked invoice in the system, empty for manually entered references */
  invoiceId?: string;

  /** The issue date in the `yyyy-mm-dd` shape a date input binds to */
  get issueDateFormable(): string {
    if (!this.issueDate) {
      return "";
    }

    return formatYearsMonthDay(new Date(this.issueDate));
  }

  set issueDateFormable(date: string) {
    this.issueDate = date ? new Date(date).toISOString() : undefined;
  }

  /** Whether the reference points at an invoice recorded in the system */
  get isLinked(): boolean {
    return !!this.invoiceId;
  }

  get dto(): MrfiktivPrecedingInvoiceDtoGen {
    return {
      number: this.number,
      issueDate: this.issueDate || null,
      invoiceId: this.invoiceId || null
    };
  }

  constructor(data?: Partial<MrfiktivPrecedingInvoiceViewModelGen>) {
    this.number = data?.number ?? "";
    this.issueDate = data?.issueDate;
    this.invoiceId = data?.invoiceId;
  }
}

export type IPrecedingInvoice = PrecedingInvoice;
